import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { GoogleOAuthProvider } from "@react-oauth/google";

// -----------------------------------------------------------------------------
// EAGER imports — only the two entry points that actually carry first-paint
// traffic: the marketing homepage and blog content (SEO). Keeping these
// bundled with main.js means their first paint doesn't wait on a second
// chunk network round-trip.
// -----------------------------------------------------------------------------
import Landing from "./pages/Landing";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import ProtectedRoute from "./components/ProtectedRoute";
import ServiceLineGate from "./components/ServiceLineGate";
import PreviewModeBanner from "./components/PreviewModeBanner";
import { Toaster } from "./components/ui/sonner";
import DiscountPopup from "./components/DiscountPopup";
import WhatsAppBubble from "./components/WhatsAppBubble";

// -----------------------------------------------------------------------------
// LAZY imports — heavy routes that only matter after the visitor takes an
// explicit action. Splitting these out drops the initial JS bundle from
// ~2.6 MB to ~500 KB, which is the single biggest LCP win we have on this
// site (Siteguru Pagespeed Feb 3 2026 flagged all 24 pages).
// Grouped by area so it's obvious what a chunk contains.
// -----------------------------------------------------------------------------
// Auth / legal / misc — real routes, but rarely a visitor's actual entry
// point (nearly always reached via in-app navigation after main.js is
// already loaded), unlike Landing/Blog above. Moved out of the eager
// bundle 2026-08-07: every one of the ~90 blog posts was paying to
// download this code on every load without ever using it (Siteguru
// Pagespeed flagged "reduce unused JavaScript" on 25 pages).
const CoatOfArmsLanding = lazy(() => import("./pages/CoatOfArmsLanding"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const NotFound = lazy(() => import("./pages/NotFound"));

// User dashboard & submissions
const Dashboard = lazy(() => import("./pages/Dashboard"));
const PublicGallery = lazy(() => import("./pages/PublicGallery"));
const CoatOfArmsForm = lazy(() => import("./pages/CoatOfArmsForm"));
const SubmissionDetail = lazy(() => import("./pages/SubmissionDetail"));
const ClaimCode = lazy(() => import("./pages/ClaimCode"));

// Payments / checkout
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("./pages/PaymentCancel"));
const Cart = lazy(() => import("./pages/Cart"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const CheckoutCancel = lazy(() => import("./pages/CheckoutCancel"));

// T-shirt & wanted poster editors (fabric.js, large canvases)
const WantedPoster = lazy(() => import("./pages/WantedPoster"));
const CelebrationTshirts = lazy(() => import("./pages/CelebrationTshirts"));
const CelebrationTshirtEditor = lazy(() => import("./pages/CelebrationTshirtEditor"));

// Office Legends / reveals
const OfficeLegends = lazy(() => import("./pages/OfficeLegends"));
const LegendReveal = lazy(() => import("./pages/LegendReveal"));

// Moments / time-capsule
const MomentViewer = lazy(() => import("./pages/moments/MomentViewer"));
const MomentCreator = lazy(() => import("./pages/moments/MomentCreator"));
const Vault = lazy(() => import("./pages/moments/Vault"));

// Group cards (flipbook is heavy)
const GroupCardsLanding = lazy(() => import("./pages/group-cards/GroupCardsLanding"));
const GroupCardCreate = lazy(() => import("./pages/group-cards/GroupCardCreate"));
const GroupCardSign = lazy(() => import("./pages/group-cards/GroupCardSign"));
const GroupCardPreview = lazy(() => import("./pages/group-cards/GroupCardPreview"));
const GroupCardView = lazy(() => import("./pages/group-cards/GroupCardView"));

// Admin routes (only ever loaded when an admin logs in — biggest single win)
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminSubmissions = lazy(() => import("./pages/admin/AdminSubmissions"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminUpload = lazy(() => import("./pages/admin/AdminUpload"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const AdminBlogEditor = lazy(() => import("./pages/admin/AdminBlogEditor"));
const AdminBlogCategories = lazy(() => import("./pages/admin/AdminBlogCategories"));
const AdminVideos = lazy(() => import("./pages/admin/AdminVideos"));
const AdminPayments = lazy(() => import("./pages/admin/AdminPayments"));
const AdminPricing = lazy(() => import("./pages/admin/AdminPricing"));
const AdminStripe = lazy(() => import("./pages/admin/AdminStripe"));
const AdminValidation = lazy(() => import("./pages/admin/AdminValidation"));
const AdminBackup = lazy(() => import("./pages/admin/AdminBackup"));
const AdminSubscribers = lazy(() => import("./pages/admin/AdminSubscribers"));
const AdminEtsy = lazy(() => import("./pages/admin/AdminEtsy"));
const AdminCoatOfArms = lazy(() => import("./pages/admin/AdminCoatOfArms"));
const AdminTshirtProfit = lazy(() => import("./pages/admin/AdminTshirtProfit"));
const AdminTshirtTemplates = lazy(() => import("./pages/admin/AdminTshirtTemplates"));
const AdminServiceLines = lazy(() => import("./pages/admin/AdminServiceLines"));

// Suspense fallback while a chunk is fetching. Kept minimal so the switch
// between routes feels instantaneous — a full spinner mid-navigation would
// feel worse than the ~200ms chunk fetch itself.
const RouteFallback = () => (
  <div
    data-testid="route-suspense-fallback"
    className="min-h-[60vh] flex items-center justify-center text-[#94A3B8] font-manrope text-sm"
  >
    <div className="animate-pulse">Loading…</div>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Trailing-slash redirect (Emergent Support directive, Feb 2026).
// Google + Siteguru flagged every "/route" AND "/route/" as duplicate content.
// Support asked us to normalise this client-side inside React Router rather
// than at the Cloudflare edge, so this effect strips any single trailing
// slash (except for the bare "/" homepage) and replaces the history entry
// so it doesn't add a back-button step for the visitor.
function TrailingSlashRedirect() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const { pathname, search, hash } = location;
    if (pathname.length > 1 && pathname.endsWith("/")) {
      navigate(pathname.replace(/\/+$/, "") + search + hash, { replace: true });
    }
  }, [location, navigate]);

  return null;
}

function AppRouter() {
  // Sync the browser tab title with the admin-configurable
  // `browser_tab_title` field on /api/public/settings. Runs once on mount;
  // no user-facing UI. If unset/empty, falls back to whatever index.html
  // rendered (currently "Happy Ages").
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/public/settings`);
        if (!r.ok) return;
        const data = await r.json();
        const title = (data.browser_tab_title || "").trim();
        if (!cancelled && title) {
          document.title = title;
        }
      } catch {
        // Non-fatal — leave the static index.html title in place.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <TrailingSlashRedirect />
      <PreviewModeBanner />
      <Suspense fallback={<RouteFallback />}>
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/coat-of-arms" element={<CoatOfArmsLanding />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/showcase" element={<PublicGallery />} />
      <Route path="/gallery" element={<Dashboard />} />
      {/* Keep /dashboard as alias for backwards compatibility */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/createarms" element={<CoatOfArmsForm />} />
      <Route path="/submission/:id" element={<SubmissionDetail />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin/submissions" element={<AdminSubmissions />} />
      <Route path="/admin/reviews" element={<AdminReviews />} />
      <Route path="/admin/users" element={<AdminUsers />} />
      <Route path="/admin/upload/:id" element={<AdminUpload />} />
      <Route path="/admin/settings" element={<AdminSettings />} />
      <Route path="/admin/reports" element={<AdminReports />} />
      <Route path="/admin/blog" element={<AdminBlog />} />
      <Route path="/admin/blog/new" element={<AdminBlogEditor />} />
      <Route path="/admin/blog/edit/:postId" element={<AdminBlogEditor />} />
      <Route path="/admin/blog/categories" element={<AdminBlogCategories />} />
      <Route path="/admin/videos" element={<AdminVideos />} />
      <Route path="/admin/payments" element={<AdminPayments />} />
      <Route path="/admin/pricing" element={<AdminPricing />} />
      <Route path="/admin/tshirt-profit" element={<AdminTshirtProfit />} />
      <Route path="/admin/tshirt-templates" element={<AdminTshirtTemplates />} />
      <Route path="/admin/service-lines" element={<AdminServiceLines />} />
      <Route path="/admin/stripe" element={<AdminStripe />} />
      <Route path="/admin/validation" element={<AdminValidation />} />
      <Route path="/admin/backup" element={<AdminBackup />} />
      <Route path="/admin/subscribers" element={<AdminSubscribers />} />
      <Route path="/admin/etsy" element={<AdminEtsy />} />
      <Route path="/admin/coat-of-arms" element={<AdminCoatOfArms />} />
      <Route path="/claim" element={<ClaimCode />} />
      <Route path="/claim/:code" element={<ClaimCode />} />
      <Route path="/wanted-poster" element={<WantedPoster />} />
      {/* Birthday & Milestone Reminders — line is DISABLED by default so the gate renders "Coming Soon".
          When we build the real page later, replace the second arg with the real component. */}
      <Route path="/reminders" element={<ServiceLineGate lineId="reminders"><div /></ServiceLineGate>} />
      <Route path="/office-legends" element={<ServiceLineGate lineId="office"><OfficeLegends /></ServiceLineGate>} />
      <Route path="/legend-reveal/:revealId" element={<ServiceLineGate lineId="office"><LegendReveal /></ServiceLineGate>} />
      <Route path="/corporate" element={<Navigate to="/office-legends" replace />} />
      {/* Happy Moments - Shareable Experiences */}
      <Route path="/happy-moments" element={<MomentCreator />} />
      <Route path="/moments/:momentId" element={<MomentViewer />} />
      <Route path="/vault" element={<ProtectedRoute><Vault /></ProtectedRoute>} />
      {/* Group Cards - Collaborative Signing */}
      <Route path="/group-cards" element={<GroupCardsLanding />} />
      <Route path="/group-cards/create" element={<GroupCardCreate />} />
      <Route path="/group-cards/:cardId/sign" element={<GroupCardSign />} />
      <Route path="/group-cards/:cardId/preview" element={<GroupCardPreview />} />
      <Route path="/group-cards/:cardId/view" element={<GroupCardView />} />
      {/* Celebration T-Shirts */}
      <Route path="/celebration-tshirts" element={<ServiceLineGate lineId="tshirts"><CelebrationTshirts /></ServiceLineGate>} />
      <Route path="/celebration-tshirts/editor/:templateId" element={<ServiceLineGate lineId="tshirts"><CelebrationTshirtEditor /></ServiceLineGate>} />
      {/* Cart & Checkout */}
      <Route path="/cart" element={<Cart />} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
      <Route path="/checkout/cancel" element={<CheckoutCancel />} />
      {/* Catch-all 404 — MUST be last so all other routes match first */}
      <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <HelmetProvider>
        <div className="App">
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-right" richColors />
            <DiscountPopup />
            <WhatsAppBubble />
          </BrowserRouter>
        </div>
      </HelmetProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
